<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch, readonly } from 'vue';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../../cross-app-frontend/sheet';
import { Button } from '../../../cross-app-frontend/button';
import { Input } from '../../../cross-app-frontend/input';
import { ScrollArea } from '../../../cross-app-frontend/scroll-area';
import { Badge } from '../../../cross-app-frontend/badge';
import { actions } from '@/services/actions';
import type { FacilityID } from '../../../tri-shared/types/db';
import { UsageTypeNames } from '../../../tri-shared/services/usage-types';
// @ts-expect-error
import womanSquarePng from '@/assets/woman_square.png?url';

interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  sources?: Array<{
    filename: string;
    file_id: string;
    text?: string;
    score?: number;
    originalIndex?: number;
  }>;
}

withDefaults(
  defineProps<{
    triggerVariant?: 'default' | 'outline' | 'ghost' | 'secondary';
    triggerSize?: 'sm' | 'default' | 'lg';
    triggerText?: string;
    triggerIcon?: string;
    endpoint?: string;
    systemPrompt?: string;
    placeholder?: string;
    title?: string;
    facilityID: FacilityID;
    year: number;
  }>(),
  {
    triggerVariant: 'outline',
    triggerSize: 'default',
    triggerText: 'Ask AI',
    triggerIcon: 'fa-robot',
    endpoint: '/api/ai/chat',
    systemPrompt: `You are a helpful but conversational TRI assistant. Respond naturally. When the user says 'thanks' or similar, respond warmly without repeating prior information.`,
    placeholder: 'Ask me anything...',
    title: 'TRI Assistant',
  },
);

const isOpen = ref(false);
const isLoading = ref(false);
const currentMessage = ref('');
const messages = ref<ChatMessage[]>([]);
const messagesContainer = ref<HTMLElement>();
const expandedSources = ref<Set<string>>(new Set());
const selectedContext = ref<
  Array<{
    type: 'product' | 'threshold' | 'release' | 'process';
    id: string;
    label: string;
    data: any;
  }>
>([]);
const sourceSearchTerms = ref<Record<string, string>>({});

// Add suggested questions
const suggestedQuestions = ref([
  {
    id: 'thresholds-exceeded',
    question: 'What are the applicability criteria for TRI Reporting?',
    icon: 'fa-exclamation-triangle',
    category: 'Compliance',
  },
  {
    id: 'form-r-requirements',
    question: 'What are the reporting thresholds for special chemicals of concern?',
    icon: 'fa-file-text',
    category: 'Reporting',
  },
  {
    id: 'release-calculations',
    question: 'How do I calculate releases for my manufacturing processes?',
    icon: 'fa-calculator',
    category: 'Calculations',
  },
  {
    id: 'activity-types',
    question:
      'How do I determine whether my facility has 10 full-time employees, and why does this matter for TRI reporting?',
    icon: 'fa-tags',
    category: 'Classification',
  },
  {
    id: 'de-minimis',
    question: 'What is the de minimis exemption, and how does it apply to my facility?',
    icon: 'fa-scale-balanced',
    category: 'Exemptions',
  },
  {
    id: 'pfas-reporting',
    question: 'What are the new PFAS reporting requirements for TRI?',
    icon: 'fa-flask',
    category: 'New Requirements',
  },
]);

const canSend = computed(() => currentMessage.value.trim().length > 0 && !isLoading.value);

// Load persisted chat on component mount
onMounted(() => {
  loadPersistedChat();
});

// Watch for changes and persist chat
watch(
  messages,
  () => {
    persistChat();
  },
  { deep: true },
);

// Watch for sheet opening and scroll to bottom
watch(isOpen, (newValue) => {
  if (newValue) {
    // Use a longer delay to ensure the DOM is fully rendered
    nextTick(() => {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    });
  }
});

function loadPersistedChat() {
  try {
    const saved = localStorage.getItem('tri-ai-chat');
    if (saved) {
      const parsed = JSON.parse(saved);
      messages.value = parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch (error) {
    console.warn('Failed to load persisted chat:', error);
  }
}

function persistChat() {
  try {
    localStorage.setItem('tri-ai-chat', JSON.stringify(messages.value));
  } catch (error) {
    console.warn('Failed to persist chat:', error);
  }
}

function selectSuggestedQuestion(question: string) {
  currentMessage.value = question;
  sendMessage();
}

function buildContextualMessage(userMessage: string): string {
  if (selectedContext.value.length === 0) {
    return `SEARCH QUERY: ${userMessage}

RESPOND TO: ${userMessage}`;
  }

  const contextData = selectedContext.value
    .map((item) => {
      switch (item.type) {
        case 'product': {
          const product = item.data;
          return `Product: ${product.chemical_name} (${product.cas_number}) - Activity: ${UsageTypeNames[product.activity_type as keyof typeof UsageTypeNames]} - Amount: ${product.amount_lbs ? `${product.amount_lbs} lbs` : 'Not specified'}`;
        }
        case 'threshold': {
          const threshold = item.data;
          return `Threshold: ${threshold.chemical_name} (${threshold.cas_number}) - Type: ${threshold.threshold_type} - Amount: ${threshold.threshold_amount_lbs} lbs - Status: ${threshold.exceeded ? 'Exceeded' : 'Not Exceeded'}`;
        }
        case 'release': {
          const release = item.data;
          return `Release: ${release.chemical_name} (${release.cas_number}) - Form R Line: ${release.form_r_line} - Amount: ${release.amount_lbs} lbs - Basis: ${release.basis_of_estimate}`;
        }
        case 'process': {
          const process = item.data;
          return `Process: ${process.process_name} - Description: ${process.process_description || 'Not specified'}`;
        }
        default:
          return '';
      }
    })
    .filter(Boolean);

  return `SEARCH QUERY: ${userMessage}

CONTEXT DATA for this facility:
${contextData.join('\n')}

RESPOND TO: ${userMessage}`;
}

// Override sendMessage to include context
async function sendMessage() {
  if (!canSend.value) return;

  const userMessageText = currentMessage.value.trim();
  const contextualMessage = buildContextualMessage(userMessageText);

  const userMessage: ChatMessage = {
    id: Date.now().toString(),
    content: userMessageText, // Show original message to user
    role: 'user',
    timestamp: new Date(),
  };

  messages.value.push(userMessage);
  currentMessage.value = '';
  isLoading.value = true;

  // Clear context after sending
  selectedContext.value = [];

  // Scroll to bottom after adding user message
  await nextTick();
  scrollToBottom();

  try {
    // Build conversation context for continuity
    const conversationHistory = messages.value
      .slice(-6) // Keep focused conversation history
      .map((msg) => `${msg.role}: ${msg.content}`)
      .join('\n');

    // Improved prompt structure with clear separation
    let finalMessage;
    if (messages.value.length > 1) {
      finalMessage = `${contextualMessage}

PREVIOUS CONVERSATION (for context only):
${conversationHistory}

CURRENT USER MESSAGE (respond to this): ${userMessageText}`;
    } else {
      // For first message, structure it to prioritize search
      finalMessage = `SEARCH QUERY FOR RETRIEVAL: ${userMessageText}

${contextualMessage}

FIRST MESSAGE - respond directly to: ${userMessageText}`;
    }

    console.group('🤖 AI Request Details');
    console.info('📤 Sending message to AI:');
    console.info('- User message:', userMessageText);
    console.info('- Contextual message:', contextualMessage);
    console.info('- Final message length:', finalMessage.length);
    console.info('- Final message (first 500 chars):', finalMessage.substring(0, 500));
    console.info('- Conversation history length:', conversationHistory.length);
    console.groupEnd();

    // Create a placeholder message for streaming updates
    const assistantMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      sources: [],
    };

    // Add the placeholder message to the chat
    messages.value.push(assistantMessage);

    // Get the index of the assistant message for direct updates
    const assistantMessageIndex = messages.value.length - 1;

    // Store annotations for processing after completion
    const accumulatedAnnotations: any[] = [];

    // Start the streaming request
    await actions.ai.askQuestion(finalMessage, (...callbackParams) => {
      const [updatedText, done, sources] = callbackParams;

      // Process sources and annotations
      if (sources && Array.isArray(sources)) {
        sources.forEach((source: any) => {
          if (source.annotations && Array.isArray(source.annotations)) {
            accumulatedAnnotations.push(...source.annotations);
          }
        });
      }

      // Update the assistant message content directly
      messages.value[assistantMessageIndex] = {
        ...messages.value[assistantMessageIndex],
        content: updatedText,
      };

      // Update sources if we have them
      if (sources && sources.length > 0) {
        messages.value[assistantMessageIndex] = {
          ...messages.value[assistantMessageIndex],
          sources: sources.map((source: any) => ({
            filename: source.filename,
            file_id: source.file_id,
            text: source.text || source.content || 'Source text not available',
            score: source.score || 0,
          })),
        };
      }

      // Scroll to bottom to follow the streaming text - ensure it happens on every update
      nextTick(() => scrollToBottom());

      // Mark the request as complete when done
      if (done) {
        // Process citations after completion
        if (accumulatedAnnotations.length > 0) {
          const contentWithCitations = processCitations(updatedText, accumulatedAnnotations);
          messages.value[assistantMessageIndex] = {
            ...messages.value[assistantMessageIndex],
            content: contentWithCitations,
          };
        }
        isLoading.value = false;
      }
    });
    scrollToBottom();
  } catch (error) {
    console.error('Error sending message:', error);

    // Handle different error types with user-friendly messages
    let errorContent = 'Sorry, I encountered an error processing your request. Please try again.';

    if (error instanceof Error) {
      if (error.message.includes('User must be logged in')) {
        errorContent = 'Please log in to use the AI assistant.';
      } else if (error.message.includes('API key')) {
        errorContent = 'AI service is currently unavailable. Please try again later.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorContent = 'Network error occurred. Please check your connection and try again.';
      }
    }

    // Remove the broken assistant message and add error message
    if (messages.value[messages.value.length - 1]?.role === 'assistant') {
      messages.value.pop();
    }

    const errorMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      content: errorContent,
      role: 'assistant',
      timestamp: new Date(),
    };

    messages.value.push(errorMessage);
    scrollToBottom();
  } finally {
    isLoading.value = false;
  }
}

function processCitations(content: string, annotations: any[]): string {
  if (!annotations || annotations.length === 0) return content;

  // Sort annotations by index in reverse order to avoid index shifting during insertion
  const sortedAnnotations = [...annotations].sort((a, b) => b.index - a.index);

  let processedContent = content;

  // Create a map of citation index to citation number and file info
  const citationIndexToInfo = new Map();

  // First pass: assign citation numbers based on order in text (by index)
  const indexSortedAnnotations = [...annotations].sort((a, b) => a.index - b.index);

  indexSortedAnnotations.forEach((annotation, index) => {
    citationIndexToInfo.set(annotation.index, {
      citationNumber: index + 1,
      filename: annotation.filename,
      file_id: annotation.file_id,
    });
  });

  // Second pass: insert citations in reverse order to avoid index shifting
  for (const annotation of sortedAnnotations) {
    if (annotation.type === 'file_citation') {
      const info = citationIndexToInfo.get(annotation.index);
      if (info) {
        const citationLink = `<cite data-file-id="${info.file_id}" data-filename="${info.filename}" style="cursor: pointer; color: #2563eb; text-decoration: underline; font-size: 0.8em; vertical-align: super;">[${info.citationNumber}]</cite>`;

        processedContent =
          processedContent.slice(0, annotation.index) + citationLink + processedContent.slice(annotation.index);
      }
    }
  }

  return processedContent;
}

function formatMessageContent(content: string): string {
  // First, convert markdown-style links to HTML links
  let processedContent = content.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1 <i class="fa-light fa-external-link"></i></a>',
  );

  // Format bold text (**text**) before list processing
  processedContent = processedContent.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Format italic text (*text*)
  processedContent = processedContent.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Process line by line to handle lists properly
  const lines = processedContent.split('\n');
  const processedLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Handle numbered lists with bold headings
    const numberedBoldMatch = line.match(/^(\d+\.\s+)(<strong>[^<]+<\/strong>:?)\s*(.*)$/);
    if (numberedBoldMatch) {
      const [, number, heading, content] = numberedBoldMatch;
      processedLines.push(
        `<div class="formatted-list-item"><span class="list-number">${number}</span><span class="list-heading">${heading}</span> <span class="list-content">${content}</span></div>`,
      );
      continue;
    }

    // Handle regular numbered lists
    const numberedMatch = line.match(/^(\d+\.\s+)(.*)$/);
    if (numberedMatch) {
      const [, number, content] = numberedMatch;
      processedLines.push(
        `<div class="formatted-list-item"><span class="list-number">${number}</span><span class="list-content">${content}</span></div>`,
      );
      continue;
    }

    // Handle bullet points with bold headings
    const bulletBoldMatch = line.match(/^(-\s+)(<strong>[^<]+<\/strong>:?)\s*(.*)$/);
    if (bulletBoldMatch) {
      const [, bullet, heading, content] = bulletBoldMatch;
      processedLines.push(
        `<div class="formatted-list-item"><span class="bullet-point">${bullet}</span><span class="list-heading">${heading}</span> <span class="list-content">${content}</span></div>`,
      );
      continue;
    }

    // Handle regular bullet points
    const bulletMatch = line.match(/^(-\s+)(.*)$/);
    if (bulletMatch) {
      const [, bullet, content] = bulletMatch;
      processedLines.push(
        `<div class="formatted-list-item"><span class="bullet-point">${bullet}</span><span class="list-content">${content}</span></div>`,
      );
      continue;
    }

    // Regular line - keep as is
    processedLines.push(line);
  }

  processedContent = processedLines.join('\n');

  // Convert double line breaks to paragraph breaks, but preserve list formatting
  processedContent = processedContent.replace(/\n\n+/g, '</p><p>');

  // Wrap in paragraph tags if not already wrapped and not a list
  if (!processedContent.includes('<div class="formatted-list-item">') && !processedContent.startsWith('<p>')) {
    processedContent = '<p>' + processedContent + '</p>';
  }

  // Clean up empty paragraphs
  processedContent = processedContent.replace(/<p><\/p>/g, '');

  // Fix paragraph breaks around lists
  processedContent = processedContent.replace(
    /<\/p><div class="formatted-list-item">/g,
    '</p>\n<div class="formatted-list-item">',
  );
  processedContent = processedContent.replace(/<\/div><p>/g, '</div>\n<p>');

  // Then process math content
  processedContent = renderInlineMath(processedContent);

  // Handle citation clicks by adding event listeners after DOM update
  nextTick(() => {
    const cites = document.querySelectorAll('cite[data-file-id]');
    cites.forEach((cite) => {
      cite.addEventListener('click', handleCitationClick);
    });
  });

  return processedContent;
}

function renderInlineMath(input: string): string {
  let processed = input;

  // Handle LaTeX display math \[ ... \] (multiline support with flags)
  processed = processed.replace(/\\\[\s*\n?(.+?)\n?\s*\\\]/gs, (_, expr) => {
    return `<div class="math-display">${formatSimpleMath(expr.trim())}</div>`;
  });

  // Handle LaTeX inline math \( ... \)
  processed = processed.replace(/\\\((.+?)\\\)/g, (_, expr) => {
    return `<span class="math-inline">${formatSimpleMath(expr)}</span>`;
  });

  // Handle double dollar sign math $$ ... $$ (multiline support)
  processed = processed.replace(/\$\$\s*\n?(.+?)\n?\s*\$\$/gs, (_, expr) => {
    return `<div class="math-display">${formatSimpleMath(expr.trim())}</div>`;
  });

  // Handle single dollar sign math $ ... $ (inline only, no newlines)
  processed = processed.replace(/\$([^$\n]+)\$/g, (_, expr) => {
    return `<span class="math-inline">${formatSimpleMath(expr)}</span>`;
  });

  return processed;
}

function formatSimpleMath(expr: string): string {
  return expr
    .replace(/\*\*/g, '^')
    .replace(/\*/g, '×')
    .replace(/\\\\/g, '<br>')
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span class="math-fraction">$1/$2</span>');
}

function handleCitationClick(e: Event) {
  e.preventDefault();
  const cite = e.target as HTMLElement;
  const fileId = cite.getAttribute('data-file-id');
  const filename = cite.getAttribute('data-filename');

  if (fileId && filename) {
    const url = generateDocumentUrl(filename);
    if (url) {
      window.open(url, '_blank');
    } else {
      console.warn('Could not generate URL for document:', filename);
    }
  }
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      // Try to access the scrollable element in different ways
      let scrollElement: any = messagesContainer.value;

      // If it's a Vue component, try to get the DOM element
      if (scrollElement && typeof scrollElement === 'object' && '$el' in scrollElement) {
        scrollElement = scrollElement.$el;
      }

      // Try to find the actual scrollable viewport
      if (scrollElement && typeof scrollElement.querySelector === 'function') {
        const viewport =
          scrollElement.querySelector('[data-radix-scroll-area-viewport]') ||
          scrollElement.querySelector('[data-radix-scroll-area-content]') ||
          scrollElement.querySelector('.scroll-area-viewport') ||
          scrollElement.querySelector('.overflow-auto') ||
          scrollElement;

        if (viewport) {
          scrollElement = viewport;
        }
      }

      // Scroll to bottom
      if (scrollElement) {
        if (typeof scrollElement.scrollTo === 'function') {
          scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: 'smooth' });
        } else {
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      }
    }
  });
}

function clearChat() {
  messages.value = [];
  expandedSources.value.clear();
  sourceSearchTerms.value = {};
  localStorage.removeItem('tri-ai-chat');
}

function handleKeyPress(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    sendMessage();
  }
}

function formatTime(date: Date) {
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toggleSourceExpansion(messageId: string, sourceIndex: number) {
  const key = `${messageId}-${sourceIndex}`;
  if (expandedSources.value.has(key)) {
    expandedSources.value.delete(key);
  } else {
    expandedSources.value.add(key);
  }
}

function isSourceExpanded(messageId: string, sourceIndex: number | undefined): boolean {
  if (messageId === undefined || sourceIndex === undefined) return false;
  return expandedSources.value.has(`${messageId}-${sourceIndex}`);
}

function getSourceSearchTerm(messageId: string): string {
  return sourceSearchTerms.value[messageId] || '';
}

function expandAllSourcesForMessage(messageId: string) {
  const message = messages.value.find((m) => m.id === messageId);
  if (message?.sources) {
    message.sources.forEach((_, index) => {
      const key = `${messageId}-${index}`;
      expandedSources.value.add(key);
    });
  }
}

function getFilteredSources(sources: ChatMessage['sources'], messageId: string) {
  if (!sources) return [];

  const searchTerm = getSourceSearchTerm(messageId);
  if (!searchTerm.trim()) {
    return sources.map((source, index) => ({ ...source, originalIndex: index }));
  }

  // Auto-expand all sources when search is active
  sources.forEach((_, index) => {
    const key = `${messageId}-${index}`;
    expandedSources.value.add(key);
  });

  return sources
    .map((source, index) => ({ ...source, originalIndex: index }))
    .filter(
      (source) =>
        source.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (source.text && source.text.toLowerCase().includes(searchTerm.toLowerCase())),
    );
}

function highlightSearchTerm(text: string, searchTerm: string): string {
  if (!searchTerm.trim()) return text;

  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.replace(regex, '<mark>$1</mark>');
}

function formatDocumentName(filename: string): string {
  // Remove file extensions and clean up common patterns
  const name = filename.replace(/\.(pdf|txt|html)$/i, '');

  // Map specific filenames to meaningful names
  const documentMap: Record<string, string> = {
    'tri-guidance': 'TRI Program Guidance',
    'form-r-instructions': 'Form R Instructions',
    'consolidated-list-of-lists': 'Consolidated List of Lists',
    'pfas-ndaa-guidance': 'PFAS NDAA Guidance',
  };

  // Check for exact match first
  const exactMatch = documentMap[name.toLowerCase()];
  if (exactMatch) return exactMatch;

  // Apply general formatting rules
  return name
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function generateDocumentUrl(filename: string): string | null {
  // Remove file extensions
  const cleanName = filename.replace(/\.(pdf|txt|html)$/i, '');

  // Special URL mappings based on browser history patterns
  const urlMap: Record<string, string> = {
    'tri-guidance': 'https://www.epa.gov/toxics-release-inventory-tri-program/tri-program-guidance',
    'form-r-instructions': 'https://www.epa.gov/toxics-release-inventory-tri-program/form-r-and-form-instructions',
    'consolidated-list-of-lists': 'https://www.epa.gov/epcra/consolidated-list-lists',
    'pfas-ndaa-guidance':
      'https://www.epa.gov/toxics-release-inventory-tri-program/addition-certain-pfas-tri-national-defense-authorization-act',
  };

  // Check for exact match first
  const exactUrl = urlMap[cleanName.toLowerCase()];
  if (exactUrl) return exactUrl;

  // For files that look like standard TRI guidance, try to construct URL
  if (cleanName.toLowerCase().includes('tri guidance') || cleanName.toLowerCase().includes('tri-guidance')) {
    return 'https://www.epa.gov/toxics-release-inventory-tri-program/tri-program-guidance';
  }

  // For consolidated lists and other EPA documents
  if (cleanName.toLowerCase().includes('consolidated-list-of-lists')) {
    return 'https://www.epa.gov/epcra/consolidated-list-lists';
  }

  if (cleanName.toLowerCase().includes('pfas') && cleanName.toLowerCase().includes('ndaa')) {
    return 'https://www.epa.gov/toxics-release-inventory-tri-program/addition-certain-pfas-tri-national-defense-authorization-act';
  }

  if (cleanName.toLowerCase().includes('2024qa')) {
    return 'https://guideme.epa.gov/ords/guideme_ext/f?p=guideme:qa-search';
  }

  // Return null if no URL can be generated
  return null;
}

// Expose methods for parent components
defineExpose({
  open: () => {
    isOpen.value = true;
  },
  close: () => {
    isOpen.value = false;
  },
  isOpen: readonly(isOpen),
});
</script>

<template>
  <Sheet v-model:open="isOpen">
    <SheetTrigger asChild>
      <slot name="trigger">
        <Button :variant="triggerVariant" :size="triggerSize">
          <i v-if="triggerIcon" :class="`fa-light ${triggerIcon} mr-2`"></i>
          {{ triggerText }}
        </Button>
      </slot>
    </SheetTrigger>

    <SheetContent
      class="flex h-full w-full flex-col overflow-hidden sm:max-w-[800px] lg:max-w-[900px] xl:max-w-[1000px]"
    >
      <SheetHeader class="shrink-0">
        <div class="flex items-center justify-between">
          <SheetTitle class="flex items-center gap-2">
            <img :src="womanSquarePng" alt="Ivy - TRI Assistant" class="h-7 w-7 rounded-full opacity-90" />
            {{ title }} - Ivy
          </SheetTitle>
          <div class="flex items-center gap-2">
            <Badge v-if="messages.length > 0" variant="secondary" class="text-xs">
              {{ messages.length }} messages
            </Badge>
            <Button v-if="messages.length > 0" variant="ghost" size="sm" @click="clearChat" title="Clear chat">
              <i class="fa-light fa-trash text-muted-foreground"></i>
            </Button>
          </div>
        </div>
      </SheetHeader>

      <!-- Welcome State -->
      <div v-if="messages.length === 0" class="flex flex-1 flex-col items-center justify-center overflow-auto p-6">
        <div class="mx-auto w-full max-w-4xl text-center">
          <div class="mb-6 flex justify-center">
            <div class="flex h-20 w-20 items-center justify-center rounded-full border-4 border-blue-200 bg-white p-1">
              <img :src="womanSquarePng" alt="Ivy - TRI Assistant" class="h-full w-full rounded-full object-cover" />
            </div>
          </div>
          <h3 class="mb-2 text-xl font-medium">Hi, I'm Ivy!</h3>
          <p class="text-muted-foreground mx-auto mb-8 max-w-lg">
            I'm your TRI assistant. I can help you with EPA guidance, regulatory questions, calculations, and compliance
            requirements.
          </p>

          <!-- Suggested Questions -->
          <div class="w-full">
            <p class="text-muted-foreground mb-4 text-sm font-medium">Try asking me about:</p>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Button
                v-for="question in suggestedQuestions.slice(0, 6)"
                :key="question.id"
                variant="outline"
                class="hover:bg-muted/50 h-auto justify-start p-4 text-left whitespace-normal transition-colors"
                @click="selectSuggestedQuestion(question.question)"
              >
                <i :class="`fa-light ${question.icon} text-muted-foreground mt-0.5 mr-3 shrink-0`"></i>
                <div class="flex-1 text-sm leading-relaxed">{{ question.question }}</div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Messages -->
      <div v-else class="flex flex-1 flex-col overflow-hidden">
        <ScrollArea ref="messagesContainer" class="flex-1 p-4">
          <div class="space-y-4">
            <div
              v-for="message in messages"
              :key="message.id"
              class="flex gap-3"
              :class="{
                'justify-end': message.role === 'user',
                'justify-start': message.role === 'assistant',
              }"
            >
              <!-- Assistant Avatar -->
              <div v-if="message.role === 'assistant'" class="shrink-0">
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full border-2 border-blue-200 bg-white p-0.5"
                >
                  <img :src="womanSquarePng" alt="Ivy" class="h-full w-full rounded-full object-cover" />
                </div>
              </div>

              <!-- Message Content -->
              <div
                class="max-w-[85%] rounded-lg px-4 py-2"
                :class="{
                  'bg-primary text-primary-foreground': message.role === 'user',
                  'bg-muted': message.role === 'assistant',
                }"
              >
                <div
                  v-if="message.role === 'assistant'"
                  class="prose prose-sm max-w-none"
                  v-html="formatMessageContent(message.content)"
                ></div>
                <div v-else>{{ message.content }}</div>

                <!-- Sources -->
                <div v-if="message.sources && message.sources.length > 0" class="mt-3 border-t border-gray-200 pt-3">
                  <div class="mb-3 flex flex-col gap-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium text-gray-600"
                        >Sources ({{ getFilteredSources(message.sources, message.id).length }})</span
                      >
                      <Button
                        v-if="message.sources.length > 1"
                        size="sm"
                        variant="ghost"
                        @click="expandAllSourcesForMessage(message.id)"
                        class="h-7 px-3 text-xs"
                      >
                        Show All
                      </Button>
                    </div>
                    <Input
                      v-model="sourceSearchTerms[message.id]"
                      placeholder="Search in sources..."
                      class="source-search-input w-full"
                    />
                  </div>

                  <div class="space-y-2">
                    <div
                      v-for="(source, index) in getFilteredSources(message.sources, message.id)"
                      :key="index"
                      class="rounded-sm border bg-white p-2 text-xs"
                    >
                      <div class="mb-1 flex items-center justify-between">
                        <span
                          class="font-medium text-blue-600"
                          v-html="
                            highlightSearchTerm(formatDocumentName(source.filename), getSourceSearchTerm(message.id))
                          "
                        ></span>
                        <Button
                          size="sm"
                          variant="ghost"
                          @click="toggleSourceExpansion(message.id, source.originalIndex ?? index)"
                          class="h-6 px-2 text-xs"
                        >
                          {{ isSourceExpanded(message.id, source.originalIndex ?? index) ? 'Hide' : 'Show' }}
                        </Button>
                      </div>

                      <div v-if="isSourceExpanded(message.id, source.originalIndex ?? index)" class="text-gray-600">
                        <div
                          class="max-h-48 overflow-y-auto rounded-sm bg-gray-50 p-3 text-xs leading-relaxed"
                          v-html="
                            highlightSearchTerm(
                              source.text || 'Source text not available',
                              getSourceSearchTerm(message.id),
                            )
                          "
                        ></div>
                      </div>
                    </div>

                    <!-- No results message -->
                    <div
                      v-if="
                        getFilteredSources(message.sources, message.id).length === 0 &&
                        getSourceSearchTerm(message.id).trim()
                      "
                      class="py-2 text-center text-xs text-gray-500"
                    >
                      No sources match your search
                    </div>
                  </div>
                </div>

                <!-- Timestamp -->
                <div class="mt-2 text-xs opacity-60">{{ formatTime(message.timestamp) }}</div>
              </div>

              <!-- User Avatar -->
              <div v-if="message.role === 'user'" class="shrink-0">
                <div class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100">
                  <i class="fa-light fa-user text-gray-600"></i>
                </div>
              </div>
            </div>

            <!-- Loading indicator inside assistant message -->
            <div
              v-if="
                isLoading &&
                messages[messages.length - 1]?.role === 'assistant' &&
                !messages[messages.length - 1]?.content
              "
              class="flex justify-start"
            >
              <div class="shrink-0">
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full border-2 border-blue-200 bg-white p-0.5"
                >
                  <img :src="womanSquarePng" alt="Ivy" class="h-full w-full rounded-full object-cover" />
                </div>
              </div>
              <div class="bg-muted ml-3 rounded-lg px-4 py-2">
                <div class="flex items-center space-x-1">
                  <div class="h-2 w-2 animate-bounce rounded-full bg-gray-400"></div>
                  <div class="h-2 w-2 animate-bounce rounded-full bg-gray-400" style="animation-delay: 0.1s"></div>
                  <div class="h-2 w-2 animate-bounce rounded-full bg-gray-400" style="animation-delay: 0.2s"></div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>

      <!-- Input Area -->
      <div class="shrink-0 border-t p-4">
        <div class="flex gap-2">
          <Input
            v-model="currentMessage"
            :placeholder
            @keypress="handleKeyPress"
            :disabled="isLoading"
            class="flex-1"
          />
          <Button @click="sendMessage" :disabled="!canSend" size="sm">
            <i class="fa-light fa-paper-plane"></i>
          </Button>
        </div>
      </div>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
@reference "../../index.css";

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.animate-bounce {
  animation: bounce 1.4s infinite;
}

:deep(mark) {
  @apply rounded-sm bg-yellow-200 px-1 text-yellow-900;
}

/* Mathematical content styling */
:deep(.math-inline) {
  @apply rounded-sm border bg-slate-50 px-1 py-1 font-mono text-sm;
}

:deep(.math-display) {
  @apply my-4 rounded-r border-l-4 border-blue-200 bg-slate-50 p-4 text-center font-mono text-base;
}

/* Markdown link styling */
:deep(a) {
  @apply underline underline-offset-2;
}

:deep(a i.fa-external-link) {
  @apply ml-1 text-xs;
}

/* Citation styling */
:deep(cite) {
  cursor: pointer;
  color: #2563eb;
  text-decoration: underline;
  font-size: 0.8em;
  vertical-align: super;
}

/* Prose styling for AI responses */
:deep(.prose) {
  @apply text-sm;
}

:deep(.prose p) {
  @apply my-2;
}

:deep(.prose ul) {
  @apply my-3 list-inside list-disc space-y-2;
}

:deep(.prose ol) {
  @apply my-4 list-inside list-decimal space-y-3;
}

:deep(.prose li) {
  @apply leading-relaxed;
}

:deep(.prose strong) {
  @apply font-semibold text-slate-900;
}

:deep(.prose code) {
  @apply rounded-sm bg-slate-100 px-2 py-1 font-mono text-sm;
}

/* Source search input styling */
.source-search-input {
  background: white !important;
  border: 1px solid #d1d5db !important;
  border-radius: 6px !important;
  padding: 8px 12px !important;
  font-size: 14px !important;
  height: 36px !important;
  width: 100% !important;
  color: #374151 !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1) !important;
}

.source-search-input:focus {
  outline: 2px solid #3b82f6 !important;
  outline-offset: -2px !important;
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

.source-search-input::placeholder {
  color: #9ca3af !important;
  font-size: 13px !important;
}

/* Formatted content styling */
:deep(.formatted-list-item) {
  margin: 12px 0;
  line-height: 1.6;
  max-width: 100%;
}

:deep(.list-number) {
  font-weight: 500;
  color: #374151;
  display: inline;
}

:deep(.bullet-point) {
  font-weight: 500;
  color: #374151;
  display: inline;
}

:deep(.list-heading) {
  font-weight: 600;
  color: #1f2937;
  display: inline;
}

:deep(.list-content) {
  display: inline;
  line-height: 1.6;
  color: #374151;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

:deep(.formatted-list-item strong) {
  font-weight: 600;
  color: #1f2937;
}

:deep(.formatted-list-item em) {
  font-style: italic;
  color: #4b5563;
}
</style>
